// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
//AI GENERATED CODE
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
// WebAdminPanelReact.tsx
// ------------------------------------------------------------
// UI панель: Connections / Brandings / Locales (Name → locale)
// Отрисовывает дерево, детали выбранного узла и поддерживает refresh.
// ------------------------------------------------------------
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { UserWebStorageInstance } from '../../../user-web-storage';
import { WebAdminMsgType } from '@shared/utils/WebAdmin/WebAdminMsgType';
// ---------------- Icons
const Icon = {
    ChevronDown: () => (React.createElement("svg", { width: "14", height: "14", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" },
        React.createElement("polyline", { points: "6 9 12 15 18 9" }))),
    ChevronRight: () => (React.createElement("svg", { width: "14", height: "14", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" },
        React.createElement("polyline", { points: "9 6 15 12 9 18" }))),
    Server: () => (React.createElement("svg", { width: "14", height: "14", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" },
        React.createElement("rect", { x: "2", y: "2", width: "20", height: "8", rx: "2" }),
        React.createElement("rect", { x: "2", y: "14", width: "20", height: "8", rx: "2" }))),
    FileText: () => (React.createElement("svg", { width: "14", height: "14", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" },
        React.createElement("path", { d: "M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a 2 2 0 0 0 2-2V8z" }),
        React.createElement("polyline", { points: "14 2 14 8 20 8" }),
        React.createElement("line", { x1: "16", y1: "13", x2: "8", y2: "13" }),
        React.createElement("line", { x1: "16", y1: "17", x2: "8", y2: "17" }))),
    Terminal: () => (React.createElement("svg", { width: "14", height: "14", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" },
        React.createElement("polyline", { points: "4 17 10 11 4 5" }),
        React.createElement("line", { x1: "12", y1: "19", x2: "20", y2: "19" }))),
    Folder: () => (React.createElement("svg", { width: "14", height: "14", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" },
        React.createElement("path", { d: "M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h5l2 2h9a2 2 0 0 1 2 2z" }))),
    Logs: () => (React.createElement("svg", { width: "14", height: "14", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" },
        React.createElement("rect", { x: "3", y: "3", width: "18", height: "14", rx: "2" }),
        React.createElement("line", { x1: "7", y1: "8", x2: "17", y2: "8" }),
        React.createElement("line", { x1: "7", y1: "12", x2: "17", y2: "12" }))),
    Refresh: ({ spinning = false }) => (React.createElement("svg", { width: "14", height: "14", viewBox: "0 0 24 24", style: { transform: spinning ? 'rotate(360deg)' : undefined, transition: 'transform .6s linear' }, fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" },
        React.createElement("polyline", { points: "23 4 23 10 17 10" }),
        React.createElement("polyline", { points: "1 20 1 14 7 14" }),
        React.createElement("path", { d: "M3.51 9a9 9 0 0 1 14.13-3.36L23 10" }),
        React.createElement("path", { d: "M20.49 15a9 9 0 0 1-14.13 3.36L1 14" }))),
    Cloud: () => (React.createElement("svg", { width: "20", height: "20", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" },
        React.createElement("path", { d: "M20 17.58A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 4 16.25" })))
};
// ---------------- Utils
function groupByName(arr, getName) {
    return arr.reduce((acc, it) => {
        const k = (getName(it) || '(empty)').trim();
        (acc[k] || (acc[k] = [])).push(it);
        return acc;
    }, {});
}
function filesTotalCount(bf) {
    if (!bf)
        return 0;
    return Object.values(bf).reduce((acc, arr) => acc + (Array.isArray(arr) ? arr.length : 0), 0);
}
function findNode(root, id) {
    const stack = [...root];
    while (stack.length) {
        const n = stack.pop();
        if (n.id === id)
            return n;
        if (n.children)
            stack.push(...n.children);
    }
    return undefined;
}
// ---------------- Component
export default function WebAdminPanelReact() {
    var _a, _b, _c, _d;
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [filter, setFilter] = useState('');
    const [expanded, setExpanded] = useState({
        connections: true,
        brandings: true,
        locales: true,
        brandingFiles: true,
        logs: true
    });
    const [selected, setSelected] = useState({
        type: null,
        id: null
    });
    // logs state
    const [logsLoading, setLogsLoading] = useState(false);
    const [logsError, setLogsError] = useState(null);
    const [logs, setLogs] = useState({ lines: [] });
    // ---- load all meta
    const fetchData = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        var _a, _b, _c, _d;
        setLoading(true);
        setError(null);
        try {
            const req = { type: WebAdminMsgType.GetServerInfo };
            const resp = (yield UserWebStorageInstance.webAdmin.post(req));
            if (!(resp === null || resp === void 0 ? void 0 : resp.success)) {
                setError((resp === null || resp === void 0 ? void 0 : resp.message) || 'Failed to load server info');
                setData(null);
                return;
            }
            setData({
                connections: Array.isArray(resp.connections) ? resp.connections : [],
                brandings: Array.isArray(resp.branding) ? resp.branding : [],
                locales: (_a = resp.locales) !== null && _a !== void 0 ? _a : {},
                brandingFiles: (_b = resp.brandingFiles) !== null && _b !== void 0 ? _b : {},
                logFile: (_c = resp.logFile) !== null && _c !== void 0 ? _c : 'logs.log'
            });
        }
        catch (e) {
            setError((_d = e === null || e === void 0 ? void 0 : e.message) !== null && _d !== void 0 ? _d : 'Unknown error');
            setData(null);
        }
        finally {
            setLoading(false);
        }
    }), []);
    const refreshBranding = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        var _a;
        setLoading(true);
        setError(null);
        try {
            const req = { type: WebAdminMsgType.ReloadBranding };
            const resp = (yield UserWebStorageInstance.webAdmin.post(req));
            if (!(resp === null || resp === void 0 ? void 0 : resp.success)) {
                setError((resp === null || resp === void 0 ? void 0 : resp.message) || 'Failed to reload branding');
                return;
            }
            // TODO: Reload data?
            // if (serverResponse) {
            //     setData(serverResponse);
            //}
        }
        catch (e) {
            setError((_a = e === null || e === void 0 ? void 0 : e.message) !== null && _a !== void 0 ? _a : 'Unknown error');
        }
        finally {
            setLoading(false);
        }
    }), []);
    const fetchLogs = useCallback((fileName_1, ...args_1) => __awaiter(this, [fileName_1, ...args_1], void 0, function* (fileName, maxLines = 400) {
        var _a;
        if (!fileName)
            return;
        setLogsLoading(true);
        setLogsError(null);
        try {
            const req = { type: WebAdminMsgType.GetLogs, maxLines };
            const resp = (yield UserWebStorageInstance.webAdmin.post(req));
            if (!(resp === null || resp === void 0 ? void 0 : resp.success)) {
                setLogsError((resp === null || resp === void 0 ? void 0 : resp.message) || 'Failed to fetch logs');
                setLogs({ file: fileName, lines: [] });
                return;
            }
            const lines = Array.isArray(resp.lines) ? resp.lines : [];
            if (lines.length > 0)
                lines.unshift(`--- Showing last ${lines.length} lines from ${fileName} ---`);
            setLogs({ file: fileName, lines });
        }
        catch (e) {
            setLogsError((_a = e === null || e === void 0 ? void 0 : e.message) !== null && _a !== void 0 ? _a : 'Unknown error');
            setLogs({ file: fileName, lines: [] });
        }
        finally {
            setLogsLoading(false);
        }
    }), []);
    useEffect(() => {
        void fetchData();
    }, [fetchData]);
    // when user selects Logs root or logFile leaf — fetch
    useEffect(() => {
        if (!(data === null || data === void 0 ? void 0 : data.logFile))
            return;
        const wantsLogs = (selected.type === 'root' && selected.id === 'logs') || selected.type === 'logFile';
        if (wantsLogs)
            void fetchLogs(data.logFile, 400);
    }, [selected, data === null || data === void 0 ? void 0 : data.logFile, fetchLogs]);
    // ---- tree
    const tree = useMemo(() => {
        var _a, _b, _c, _d;
        const f = (s) => s.toLowerCase().includes(filter.toLowerCase());
        const connChildren = ((_a = data === null || data === void 0 ? void 0 : data.connections) !== null && _a !== void 0 ? _a : [])
            .filter((c) => { var _a; return !filter || f(c.Name) || ((_a = c.ServerAdress) === null || _a === void 0 ? void 0 : _a.toLowerCase().includes(filter.toLowerCase())); })
            .map((c, idx) => ({
            type: 'connection',
            id: `conn:${c.Name}:${idx}`,
            label: c.Name || c.ServerAdress || `#${idx}`,
            data: c
        }));
        const brandChildren = ((_b = data === null || data === void 0 ? void 0 : data.brandings) !== null && _b !== void 0 ? _b : [])
            .filter((b) => !filter || f(b.Name) || (b.BrandingWebName ? f(String(b.BrandingWebName)) : false))
            .map((b, idx) => ({ type: 'branding', id: `brand:${b.Name}:${idx}`, label: b.Name, data: b }));
        const localeChildren = Object.entries((_c = data === null || data === void 0 ? void 0 : data.locales) !== null && _c !== void 0 ? _c : {})
            .filter(([name]) => !filter || f(name))
            .map(([name, perLocale]) => {
            const leaves = Object.keys(perLocale !== null && perLocale !== void 0 ? perLocale : {})
                .filter((lc) => !filter || f(lc))
                .map((lc) => ({
                type: 'localeLeaf',
                id: `loc:${name}:${lc}`,
                label: lc,
                name,
                locale: lc,
                data: perLocale[lc] || {}
            }));
            return { type: 'localeGroup', id: `locgrp:${name}`, label: name, name, children: leaves };
        });
        const filesChildren = Object.entries((_d = data === null || data === void 0 ? void 0 : data.brandingFiles) !== null && _d !== void 0 ? _d : {})
            .filter(([brand]) => { var _a, _b, _c; return !filter || f(brand) || ((_c = (_b = (_a = data === null || data === void 0 ? void 0 : data.brandingFiles) === null || _a === void 0 ? void 0 : _a[brand]) === null || _b === void 0 ? void 0 : _b.some((fn) => f(fn))) !== null && _c !== void 0 ? _c : false); })
            .map(([brand, files]) => ({
            type: 'filesGroup',
            id: `filesgrp:${brand}`,
            label: brand,
            name: brand,
            files: files !== null && files !== void 0 ? files : []
        }));
        const logsChildren = (data === null || data === void 0 ? void 0 : data.logFile)
            ? [{ type: 'logFile', id: `logfile:${data.logFile}`, label: data.logFile, file: data.logFile }]
            : [];
        return [
            { type: 'root', id: 'connections', label: `Connections (${connChildren.length})`, children: connChildren },
            { type: 'root', id: 'brandings', label: `Brandings (${brandChildren.length})`, children: brandChildren },
            { type: 'root', id: 'locales', label: `Locales (${localeChildren.length})`, children: localeChildren },
            {
                type: 'root',
                id: 'brandingFiles',
                label: `Branding Files (${filesChildren.length})`,
                children: filesChildren
            },
            { type: 'root', id: 'logs', label: `Logs (${logsChildren.length})`, children: logsChildren }
        ];
    }, [data, filter]);
    const onToggle = (id) => setExpanded((p) => (Object.assign(Object.assign({}, p), { [id]: !p[id] })));
    const onSelectNode = (node) => {
        if (node.type === 'connection' ||
            node.type === 'branding' ||
            node.type === 'localeLeaf' ||
            node.type === 'localeGroup' ||
            node.type === 'filesGroup' ||
            node.type === 'logFile')
            setSelected({ type: node.type, id: node.id });
    };
    const onSelectRoot = (rootId) => {
        setSelected({ type: 'root', id: rootId });
    };
    // ---- UI
    return (React.createElement("div", { className: "web-admin-root" },
        React.createElement("div", { className: "web-admin-toolbar" },
            React.createElement("button", { onClick: () => __awaiter(this, void 0, void 0, function* () {
                    yield refreshBranding();
                }), className: "web-admin-button" },
                React.createElement(Icon.Refresh, { spinning: logsLoading }),
                " Reload branding"),
            React.createElement("input", { className: "web-admin-input", placeholder: "Filter\u2026", value: filter, onChange: (e) => setFilter(e.target.value) }),
            React.createElement("div", { className: "web-admin-stat" }, loading ? ('Loading…') : error ? (React.createElement("span", { style: { color: '#dc2626' } },
                "\u26A0 ",
                error)) : (React.createElement(React.Fragment, null,
                React.createElement("span", null,
                    "\uD83D\uDD0C ", (_a = data === null || data === void 0 ? void 0 : data.connections.length) !== null && _a !== void 0 ? _a : 0,
                    " connections"),
                React.createElement("span", null,
                    "\uD83C\uDFA8 ", (_b = data === null || data === void 0 ? void 0 : data.brandings.length) !== null && _b !== void 0 ? _b : 0,
                    " brandings"),
                React.createElement("span", null,
                    "\uD83C\uDF10 ",
                    Object.keys((_c = data === null || data === void 0 ? void 0 : data.locales) !== null && _c !== void 0 ? _c : {}).length,
                    " locale groups"),
                React.createElement("span", null,
                    "\uD83D\uDDC2 ",
                    Object.keys((_d = data === null || data === void 0 ? void 0 : data.brandingFiles) !== null && _d !== void 0 ? _d : {}).length,
                    " brands / \uD83D\uDCC4",
                    ' ',
                    filesTotalCount(data === null || data === void 0 ? void 0 : data.brandingFiles),
                    " files"),
                (data === null || data === void 0 ? void 0 : data.logFile) && React.createElement("span", null,
                    "\uD83D\uDDD2 ",
                    data.logFile))))),
        React.createElement("div", { className: "web-admin-split" },
            React.createElement("div", { className: "web-admin-card" },
                React.createElement("div", { className: "web-admin-cardHeader" }, "Explorer"),
                React.createElement("div", { className: "web-admin-cardBodyScroll" },
                    React.createElement("div", { className: "web-admin-treeContainer" },
                        React.createElement(TreeView, { tree: tree, expanded: expanded, onToggle: onToggle, onSelect: onSelectNode, onSelectRoot: onSelectRoot })))),
            React.createElement("div", { className: "web-admin-card" },
                React.createElement("div", { className: "web-admin-cardHeader" }, "Details"),
                React.createElement("div", { className: "web-admin-cardBodyScroll web-admin-p16" },
                    !selected.type && React.createElement(EmptyState, null),
                    selected.type === 'root' && selected.id === 'connections' && data && (React.createElement(ArrayGroupPanel, { title: "All connections (grouped by Name)", groups: groupByName(data.connections, (c) => c.Name) })),
                    selected.type === 'root' && selected.id === 'brandings' && data && (React.createElement(ArrayGroupPanel, { title: "All brandings (grouped by Name)", groups: groupByName(data.brandings, (b) => b.Name) })),
                    selected.type === 'root' && selected.id === 'locales' && data && (React.createElement("div", { className: "web-admin-card" },
                        React.createElement("div", { className: "web-admin-cardHeader" }, "Locale groups"),
                        React.createElement("div", { className: "web-admin-p16" },
                            React.createElement("pre", { className: "web-admin-code" }, JSON.stringify(Object.keys(data.locales), null, 2))))),
                    selected.type === 'root' && selected.id === 'brandingFiles' && data && (React.createElement("div", { className: "web-admin-card" },
                        React.createElement("div", { className: "web-admin-cardHeader" }, "All branding files"),
                        React.createElement("div", { className: "web-admin-p16" },
                            React.createElement("pre", { className: "web-admin-code" }, JSON.stringify(data.brandingFiles, null, 2))))),
                    selected.type === 'root' && selected.id === 'logs' && (React.createElement(LogsPanel, { file: data === null || data === void 0 ? void 0 : data.logFile, loading: logsLoading, error: logsError, lines: logs.lines })),
                    selected.type === 'connection' &&
                        (() => {
                            const node = findNode(tree, selected.id);
                            return node ? React.createElement(ConnectionDetailsRaw, { c: node.data }) : null;
                        })(),
                    selected.type === 'branding' &&
                        (() => {
                            const node = findNode(tree, selected.id);
                            return node ? React.createElement(BrandingDetailsRaw, { b: node.data }) : null;
                        })(),
                    selected.type === 'localeGroup' &&
                        (() => {
                            const node = findNode(tree, selected.id);
                            if (!node)
                                return null;
                            const locales = node.children.map((ch) => ch.label);
                            return (React.createElement("div", { className: "web-admin-card" },
                                React.createElement("div", { className: "web-admin-cardHeader" },
                                    "Locales for ",
                                    node.name),
                                React.createElement("div", { className: "web-admin-p16" },
                                    React.createElement("pre", { className: "web-admin-code" }, JSON.stringify(locales, null, 2)))));
                        })(),
                    selected.type === 'localeLeaf' &&
                        (() => {
                            const node = findNode(tree, selected.id);
                            return node ? (React.createElement("div", { className: "web-admin-card" },
                                React.createElement("div", { className: "web-admin-cardHeader" },
                                    "Locale ",
                                    node.name,
                                    " / ",
                                    node.locale),
                                React.createElement("div", { className: "web-admin-p16" },
                                    React.createElement("pre", { className: "web-admin-code" }, JSON.stringify(node.data, null, 2))))) : null;
                        })(),
                    selected.type === 'filesGroup' &&
                        data &&
                        (() => {
                            const node = findNode(tree, selected.id);
                            if (!node)
                                return null;
                            return React.createElement(FilesList, { brand: node.name, files: node.files });
                        })(),
                    selected.type === 'logFile' && (React.createElement(LogsPanel, { file: data === null || data === void 0 ? void 0 : data.logFile, loading: logsLoading, error: logsError, lines: logs.lines })))))));
}
// ---------------- Subcomponents
function EmptyState() {
    return (React.createElement("div", { className: "web-admin-flexCol web-admin-itemsCenter", style: { height: '100%', justifyContent: 'center', textAlign: 'center' } },
        React.createElement(Icon.Cloud, null),
        React.createElement("div", { className: "web-admin-muted", style: { fontSize: 14, marginTop: 6 } }, "Select a node to see details")));
}
function TreeView({ tree, expanded, onToggle, onSelect, onSelectRoot }) {
    return (React.createElement("ul", { style: { listStyle: 'none', margin: 0, padding: 0, userSelect: 'none' } }, tree.map((node) => (React.createElement("li", { key: node.id }, node.type === 'root' && (React.createElement(RootNode, { node: node, expanded: !!expanded[node.id], onToggle: () => onToggle(node.id), onSelectRoot: onSelectRoot }, expanded[node.id] && (React.createElement("ul", { style: { listStyle: 'none', margin: 0, padding: 0 } }, node.children.map((child) => (React.createElement("li", { key: child.id, className: "web-admin-treeIndent" }, child.type === 'connection' || child.type === 'branding' ? (React.createElement(LeafNode, { node: child, onSelect: () => onSelect(child) })) : child.type === 'localeGroup' ? (React.createElement(LocaleGroupNode, { node: child, expanded: !!expanded[child.id], onToggle: () => onToggle(child.id), onSelect: onSelect })) : child.type === 'filesGroup' ? (React.createElement(FilesGroupNode, { node: child, onSelect: () => onSelect(child) })) : child.type === 'logFile' ? (React.createElement(LogFileNode, { node: child, onSelect: () => onSelect(child) })) : null))))))))))));
}
function RootNode({ node, expanded, onToggle, children, onSelectRoot }) {
    const Left = node.id === 'connections'
        ? Icon.Server
        : node.id === 'brandings'
            ? Icon.Server
            : node.id === 'locales'
                ? Icon.Terminal
                : node.id === 'brandingFiles'
                    ? Icon.Folder
                    : Icon.Logs;
    return (React.createElement("div", null,
        React.createElement("button", { onClick: onToggle, "aria-expanded": expanded, className: "web-admin-treeRootBtn", title: "Toggle section" },
            expanded ? React.createElement(Icon.ChevronDown, null) : React.createElement(Icon.ChevronRight, null),
            React.createElement(Left, null),
            " ",
            React.createElement("span", { style: { fontSize: 14, fontWeight: 600, marginRight: 8 } }, node.label),
            React.createElement("span", { role: "button", onClick: (e) => {
                    e.stopPropagation();
                    onSelectRoot(node.id);
                }, className: "web-admin-badge", style: { marginLeft: 'auto' } }, "Show all")),
        expanded && React.createElement("div", { style: { paddingLeft: 2 } }, children)));
}
function LocaleGroupNode({ node, expanded, onToggle, onSelect }) {
    return (React.createElement("div", null,
        React.createElement("button", { onClick: onToggle, "aria-expanded": expanded, className: "web-admin-treeRootBtn" },
            expanded ? React.createElement(Icon.ChevronDown, null) : React.createElement(Icon.ChevronRight, null),
            React.createElement(Icon.Server, null),
            " ",
            React.createElement("span", { style: { fontSize: 14, fontWeight: 600 } }, node.label)),
        expanded && (React.createElement("ul", { style: { listStyle: 'none', margin: 0, paddingLeft: 16 } }, node.children.map((ch) => (React.createElement("li", { key: ch.id, className: "web-admin-treeIndent" },
            React.createElement("button", { onClick: () => onSelect(ch), className: "web-admin-treeLeafBtn" },
                React.createElement("span", { style: { opacity: 0.7 } },
                    React.createElement(Icon.FileText, null)),
                React.createElement("span", { className: "web-admin-truncate", style: { fontSize: 14 } }, ch.label)))))))));
}
function FilesGroupNode({ node, onSelect }) {
    return (React.createElement("button", { onClick: onSelect, className: "web-admin-treeLeafBtn" },
        React.createElement("span", { style: { opacity: 0.7 } },
            React.createElement(Icon.Folder, null)),
        React.createElement("span", { className: "web-admin-truncate", style: { fontSize: 14 } }, node.label)));
}
function LogFileNode({ node, onSelect }) {
    return (React.createElement("button", { onClick: onSelect, className: "web-admin-treeLeafBtn", title: "Open logs" },
        React.createElement("span", { style: { opacity: 0.7 } },
            React.createElement(Icon.Logs, null)),
        React.createElement("span", { className: "web-admin-truncate", style: { fontSize: 14 } }, node.label)));
}
function LeafNode({ node, onSelect }) {
    const Left = node.type === 'connection' ? Icon.Server : Icon.FileText;
    return (React.createElement("button", { onClick: onSelect, className: "web-admin-treeLeafBtn" },
        React.createElement("span", { style: { opacity: 0.7 } },
            React.createElement(Left, null)),
        React.createElement("span", { className: "web-admin-truncate", style: { fontSize: 14 } }, node.label)));
}
// ----- Panels
function ArrayGroupPanel({ title, groups }) {
    return (React.createElement("div", { className: "web-admin-card" },
        React.createElement("div", { className: "web-admin-cardHeader" }, title),
        React.createElement("div", { className: "web-admin-p16" },
            React.createElement("pre", { className: "web-admin-code" }, JSON.stringify(groups, null, 2)))));
}
function ConnectionDetailsRaw({ c }) {
    return (React.createElement("div", { className: "web-admin-card" },
        React.createElement("div", { className: "web-admin-cardHeader" },
            "Connection: ",
            c.Name),
        React.createElement("div", { className: "web-admin-p16" },
            React.createElement("pre", { className: "web-admin-code" }, JSON.stringify(c, null, 2)))));
}
function BrandingDetailsRaw({ b }) {
    return (React.createElement("div", { className: "web-admin-card" },
        React.createElement("div", { className: "web-admin-cardHeader" },
            "Branding: ",
            b.Name,
            b.BrandingWebName ? ` — ${b.BrandingWebName}` : ''),
        React.createElement("div", { className: "web-admin-p16" },
            React.createElement("pre", { className: "web-admin-code" }, JSON.stringify(b, null, 2)))));
}
function LogsPanel({ file, loading, error, lines }) {
    return (React.createElement("div", { className: "web-admin-card" },
        React.createElement("div", { className: "web-admin-cardHeader" },
            "Logs ",
            file ? `— ${file}` : ''),
        React.createElement("div", { className: "web-admin-p16" }, loading ? (React.createElement("div", { className: "web-admin-muted", style: { fontSize: 14 } }, "Loading logs\u2026")) : error ? (React.createElement("div", { style: { color: '#dc2626', fontSize: 14 } },
            "\u26A0 ",
            error)) : !file ? (React.createElement("div", { className: "web-admin-muted", style: { fontSize: 14 } }, "No log file configured")) : lines.length === 0 ? (React.createElement("div", { className: "web-admin-muted", style: { fontSize: 14 } }, "No log lines")) : (React.createElement("pre", { className: "web-admin-code", style: { whiteSpace: 'pre-wrap', lineHeight: '1.35' } }, lines.join('\n'))))));
}
function FilesList({ brand, files }) {
    return (React.createElement("div", { className: "web-admin-card" },
        React.createElement("div", { className: "web-admin-cardHeader" },
            "Branding Files \u2014 ",
            brand,
            " (",
            files.length,
            ")"),
        React.createElement("div", { className: "web-admin-p16" }, files.length === 0 ? (React.createElement("div", { className: "web-admin-muted", style: { fontSize: 14 } }, "No files")) : (React.createElement("ul", { style: { margin: 0, paddingLeft: 18 } }, files.map((f, i) => (React.createElement("li", { key: `${brand}:${i}`, style: { fontSize: 14, lineHeight: '22px' } }, f))))))));
}
